import React from 'react';
import { Formik } from 'formik';
import { TextField, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  Agreement,
  OldUserForm,
  ErrorCodes,
  ResponseErrorBodyDataValidationError
} from '../types';
import { useTranslation } from 'react-i18next';
import monolith from '../lib/services/monolith';
import validator from 'validator';
import { errorsByData } from '../lib/services/utils';

const useStyles = makeStyles(theme => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: '8px'
  }
}));

/**
 * @param props
 */
export default function(props: {
  agreements: Agreement[];
  handleSuccess: () => void;
  handleFailure: () => void;
  buttonText?: string;
  userId: string;
  email?: string;
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <Formik
      initialValues={
        {
          email: props.email
        } as OldUserForm
      }
      onSubmit={async (values, { setErrors, setSubmitting }) => {
        const response = await monolith({
          email: values.email,
          password: values.password,
          agreements: props.agreements,
          userId: props.userId
        });
        setSubmitting(false);
        if (!response.error) {
          props.handleSuccess();
          return;
        }
        if (response.error.code !== ErrorCodes.VALIDATION_ERROR) {
          props.handleFailure();
          return;
        }
        setErrors(
          errorsByData(
            response.error.data as ResponseErrorBodyDataValidationError,
            t
          )
        );
      }}
      validate={values => {
        let errors: { [key: string]: any } = {};
        if (!values.password) {
          errors.password = t('enterValue');
        }
        if (!values.confirmedPassword) {
          errors.confirmedPassword = t('enterValue');
        }
        if (values.password && values.confirmedPassword) {
          if (values.password !== values.confirmedPassword) {
            errors.confirmedPassword = t('errorConfirmedPassword');
          }
        }
        if (!values.email) {
          errors.email = t('enterValue');
        } else {
          if (!validator.isEmail(values.email)) {
            errors.email = t('emailError');
          }
        }
        return errors;
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        errors,
        isValid,
        isSubmitting,
        values
      }) => (
        <form onSubmit={handleSubmit} className={classes.form} noValidate>
          <TextField
            variant="standard"
            margin="normal"
            required
            fullWidth
            label={t('login')}
            name="email"
            autoComplete="email"
            autoFocus
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email || props.email}
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            variant="standard"
            margin="normal"
            required
            fullWidth
            label={t('password')}
            name="password"
            type="password"
            autoFocus
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.password}
            helperText={errors.password}
          />
          <TextField
            variant="standard"
            margin="normal"
            required
            fullWidth
            label={t('confirmedPassword')}
            name="confirmedPassword"
            type="password"
            autoFocus
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.confirmedPassword}
            helperText={errors.confirmedPassword}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ margin: '24px 0 16px' }}
            disabled={isSubmitting || !isValid}
          >
            {props.buttonText || t('changeEmailAndPassword')}
          </Button>
        </form>
      )}
    </Formik>
  );
}
